import fileDownload from 'js-file-download'
import { RequestHandler, IRequestHandler } from '../lib/RequestHandler'
import i18n from '../i18n'

export interface ReconcileResponse {
  bank_statement_id: string
}

export enum MismatchReason {
  INCORRECT_AMOUNT = 'INCORRECT_AMOUNT',
  INCORRECT_DATE = 'INCORRECT_DATE',
  INCORRECT_MERCHANT = 'INCORRECT_MERCHANT',
  DUPLICATE_TRANSACTION = 'DUPLICATE_TRANSACTION',
  MISSING_EXPENSE = 'MISSING_EXPENSE',
  OTHER = 'OTHER'
}

export interface MatchedTransaction {
  id: number
  date: string
  transaction_description: string
  merchant: string | null
  merchant_description: string | null
  amount: number
  reference: string | null
  expense_id: number | null
  expense_name: string | null
  expense_amount: number | null
  expense_date: string | null
  reasoning: string | null
  user_feedback_reason: MismatchReason | null
  user_feedback_note: string | null
  manual_expense_id: number | null
}

export interface TransactionFeedbackUpdate {
  expense_id: number | null
  user_feedback_reason: MismatchReason | null
  user_feedback_note?: string | null
}

export interface TransactionFeedbackResponse {
  id: number
  expense_id: number | null
  user_feedback_reason: MismatchReason | null
  user_feedback_note: string | null
  manual_expense_id: number | null
}

export interface ReprocessTransactionsRequest {
  transaction_ids: number[]
}

export interface ReprocessTransactionsResponse {
  statement_id: string
  transactions_to_reprocess: number
  message: string
}

export interface DownloadReconciliationRequest {
  fields: string[] | null
}

export interface DownloadReconciliationResponse {
  job_id: string
}

export interface DownloadJobStatusResponse {
  job_id: string
  status: 'pending' | 'processing' | 'uploading' | 'completed' | 'failed'
  progress: number
  download_url: string | null
  file_name: string | null
  error: string | null
  total_expenses: number | null
  processed_expenses: number | null
  current_phase: string | null
}

export enum ReconciliationPhase {
  STARTING = 'starting',
  EXTRACTION = 'extraction',
  QUERYING = 'querying',
  FILTERING = 'filtering',
  MERCHANTS = 'merchants',
  MATCHING = 'matching',
  VALIDATING = 'validating',
  SAVING = 'saving'
}

export const RECONCILIATION_PHASE_LABELS: Record<ReconciliationPhase, string> = {
  get [ReconciliationPhase.STARTING]() {
    return i18n.t('conciliation.conciliation.phases.starting')
  },
  get [ReconciliationPhase.EXTRACTION]() {
    return i18n.t('conciliation.conciliation.phases.extraction')
  },
  get [ReconciliationPhase.QUERYING]() {
    return i18n.t('conciliation.conciliation.phases.querying')
  },
  get [ReconciliationPhase.FILTERING]() {
    return i18n.t('conciliation.conciliation.phases.filtering')
  },
  get [ReconciliationPhase.MERCHANTS]() {
    return i18n.t('conciliation.conciliation.phases.merchants')
  },
  get [ReconciliationPhase.MATCHING]() {
    return i18n.t('conciliation.conciliation.phases.matching')
  },
  get [ReconciliationPhase.VALIDATING]() {
    return i18n.t('conciliation.conciliation.phases.validating')
  },
  get [ReconciliationPhase.SAVING]() {
    return i18n.t('conciliation.conciliation.phases.saving')
  }
}

export interface ReconciliationJobStatus {
  statement_id: string
  status: 'processing' | 'completed' | 'failed' | 'unknown'
  progress: number
  total_transactions: number | null
  error: string | null
  current_phase: ReconciliationPhase | null
  phase_progress: string | null
}

export enum BankStatementStatus {
  PROCESSING = 'PROCESSING',
  REPROCESSING = 'REPROCESSING',
  DONE = 'DONE',
  ERROR = 'ERROR'
}

export interface BankStatementSummary {
  id: string
  file_type: string
  is_processing: boolean
  status: BankStatementStatus
  error_code: string | null
  period_start: string | null
  period_end: string | null
  total_transactions: number
  total_matched: number
  created_at: string
}

export interface BankStatementsListResponse {
  data: BankStatementSummary[]
  total: number
}

export interface BankStatementDetail {
  id: string
  file_type: string
  is_processing: boolean
  status: BankStatementStatus
  error_code: string | null
  period_start: string | null
  period_end: string | null
  transactions: MatchedTransaction[]
  total_transactions: number
  total_matched: number
  created_at: string
}

export const ConciliationService = (props?: IRequestHandler) => ({
  requestHandler: RequestHandler(props || {}),

  async reconcile(file: File): Promise<ReconcileResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.requestHandler.post<ReconcileResponse>('conciliations/reconcile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  async getStatements(params?: { skip?: number; limit?: number; start_date?: string; end_date?: string }): Promise<BankStatementsListResponse> {
    const response = await this.requestHandler.get<BankStatementsListResponse>('conciliations/statements', { params })
    return response.data
  },

  async getStatement(id: string, params?: { skip?: number; limit?: number; sort_by?: string }): Promise<BankStatementDetail> {
    const response = await this.requestHandler.get<BankStatementDetail>(`conciliations/statements/${id}`, { params })
    return response.data
  },

  async retryStatement(id: string): Promise<ReconcileResponse> {
    const response = await this.requestHandler.post<ReconcileResponse>(`conciliations/statements/${id}/retry`)
    return response.data
  },

  async downloadStatement(id: string, fileType: string): Promise<void> {
    const response = await this.requestHandler.get<Blob>(`conciliations/statements/${id}/download`, { responseType: 'blob' })
    const contentDisposition = response.headers?.['content-disposition'] || ''
    const match = contentDisposition.match(/filename="?(.+?)"?$/)
    const fileName = match?.[1] || `statement_${id}.${fileType}`
    fileDownload(response.data, fileName)
  },

  async downloadReconciliationCsv(id: string, fields?: string[]): Promise<void> {
    const params = fields && fields.length > 0 ? { fields: fields.join(',') } : undefined
    const response = await this.requestHandler.get<Blob>(`conciliations/statements/${id}/download-csv`, {
      responseType: 'blob',
      params
    })
    const contentDisposition = response.headers?.['content-disposition'] || ''
    const match = contentDisposition.match(/filename="?(.+?)"?$/)
    const fileName = match?.[1] || i18n.t('conciliation.conciliation.csvFileName', { id })
    fileDownload(response.data, fileName)
  },

  async updateTransactionFeedback(transactionId: number, data: TransactionFeedbackUpdate): Promise<TransactionFeedbackResponse> {
    const response = await this.requestHandler.patch<TransactionFeedbackResponse>(`conciliations/transactions/${transactionId}`, data)
    return response.data
  },

  async reprocessTransactions(statementId: string, transactionIds: number[]): Promise<ReprocessTransactionsResponse> {
    const response = await this.requestHandler.post<ReprocessTransactionsResponse>(`conciliations/statements/${statementId}/reprocess`, { transaction_ids: transactionIds })
    return response.data
  },

  async downloadReconciliationExcel(statementId: string, fields?: string[]): Promise<DownloadReconciliationResponse> {
    const data: DownloadReconciliationRequest = {
      fields: fields && fields.length > 0 ? fields : null
    }
    const response = await this.requestHandler.post<DownloadReconciliationResponse>(`conciliations/statements/${statementId}/download-excel`, data)
    return response.data
  },

  async getDownloadStatus(jobId: string): Promise<DownloadJobStatusResponse> {
    const response = await this.requestHandler.get<DownloadJobStatusResponse>(`conciliations/download-jobs/${jobId}/status`)
    return response.data
  },

  async getProcessingStatus(statementId: string): Promise<ReconciliationJobStatus> {
    const response = await this.requestHandler.get<ReconciliationJobStatus>(`conciliations/statements/${statementId}/processing-status`)
    return response.data
  }
})
